import { BehaviorSubject, Subscription } from 'rxjs';
import autoBind from 'auto-bind';
import { SearchBoxQuery } from '../../src/controller/index';
import Log from '../../src/common/util/log';

const TAG = 'tabledata-bloc';

class TableRegistryClass {
	private _tables: Map<string, TableDataBloc<any>> = new Map();

	register(id: string, bloc: TableDataBloc<any>): void {
		Log.d(TAG, `Registering table: ${id}`);
		this._tables.set(id, bloc);
	}

	unregister(id: string): void {
		Log.d(TAG, `Unregistering table: ${id}`);
		this._tables.delete(id);
	}

	invalidate(id: string): Promise<void> {
		Log.d(TAG, `Invalidating table: ${id}`);
		const bloc = this._tables.get(id);
		if (bloc) {
			return bloc.updateData();
		}
		Log.d(TAG, `Table not found: ${id}`);
		return Promise.resolve();
	}

	invalidateAll(ids: string[]): Promise<void[]> {
		Log.d(TAG, `Invalidating tables: ${ids.join(', ')}`);
		return Promise.all(ids.map(id => this.invalidate(id)));
	}
}

export const TableRegistry = new TableRegistryClass();

export const TableIds = {
	GROUPS: 'groups-table',
	CONTACTS: 'contacts-table',
} as const;

export interface TableDataBlocOptions {
	autoFetch?: boolean;
	id?: string;
}

export class TableDataBloc<T extends { [key: string]: any }> {
	private _data: BehaviorSubject<T[]>;

	private _searchQuery: string;
	private _fetchMethod: () => Promise<T[]>;
	private _searchableFields: string[];
	private _id?: string;

	private _subscription: Subscription;

	get data(): BehaviorSubject<T[]> {
		return this._data;
	}

	private _dataCache: T[] = [];

	constructor(
		fetchMethod: () => Promise<T[]>,
		searchQuery: SearchBoxQuery,
		searchableFields: string[],
		options: TableDataBlocOptions = {}
	) {
		const { autoFetch = true, id } = options;

		autoBind(this);
		this._searchQuery = '';
		this._data = new BehaviorSubject([] as T[]);
		this._subscription = searchQuery.searchQuery.subscribe(e => {
			this._searchQuery = e;
			this.updateSearchResult();
		});

		this._fetchMethod = fetchMethod;
		this._searchableFields = searchableFields;
		this._id = id;

		if (id) {
			TableRegistry.register(id, this);
		}

		if (autoFetch) {
			this.updateData();
		}
	}

	destroy() {
		this._subscription.unsubscribe();
		if (this._id) {
			TableRegistry.unregister(this._id);
		}
	}

	private updateSearchResult() {
		Log.d(TAG, 'inside applySearch: ' + this._searchQuery);
		if (this._searchQuery === '') {
			return this._data.next(this._dataCache);
		}

		let list = this._dataCache.filter((e: T) => {
			let match = false;
			this._searchableFields.forEach(f => {
				match = match || (e[f] && (e[f] as string).toLowerCase().includes(this._searchQuery.toLowerCase()));
			});
			return match;
		});

		return this._data.next(list);
	}

	public async updateData(): Promise<void> {
		Log.d(TAG, 'inside fetchData');
		this._dataCache = await this._fetchMethod();
		this._data.next(this._dataCache);
		this.updateSearchResult();
	}
}
